import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import { Checkbox } from '@components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';

const generalEnquiryTypeOptions = [
  'Media',
  'Employment',
  'Other'
];

export default function GeneralEnquirySection() {
  const form = useFormContext();
  const watchGeneralEnquiryType = form.watch('generalEnquiryType');
  const watchHasSpecificDate = form.watch('hasSpecificDate');
  
  return (
    <section className="pb-12 mb-12 border-b border-border">
      <div className="text-center mb-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground block mb-3">
          Step 02
        </span>
        <h2 className="text-[28px] md:text-[32px] font-bold text-foreground uppercase tracking-[0.025em] mb-3">
          Enquiry Details
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell us more about your enquiry
        </p>
      </div>
      
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="generalEnquiryType"
          rules={{ required: 'Enquiry type is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Enquiry Type <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                    <SelectValue placeholder="Select enquiry type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {generalEnquiryTypeOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-sm">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        {watchGeneralEnquiryType === 'Other' && (
          <FormField
            control={form.control}
            name="generalEnquirySpecify"
            rules={{ required: 'Please specify your enquiry' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Please Specify <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Describe your enquiry" 
                    {...field}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="subject"
          rules={{ required: 'Subject is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Subject <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  placeholder="Brief subject line" 
                  {...field}
                  className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                />
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="tellUsMore"
          rules={{ required: 'Please provide more details' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Tell Us More <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Provide details about your enquiry..."
                  rows={6}
                  {...field}
                  className="text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)] resize-none"
                />
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="hasSpecificDate"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-semibold text-foreground">
                  Yes, this enquiry is related to a specific date
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        {watchHasSpecificDate && (
          <FormField
            control={form.control}
            name="specificDate"
            rules={{ required: 'Please provide a date' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Event Date <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    max="2030-12-31"
                    {...field}
                    value={field.value || ''}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
      </div>
    </section>
  );
}
